import * as React from 'react';
import { Box, Text, Image, IconButton, TextButton } from '@wix/design-system';
import { ArrowLeftSmall } from '@wix/wix-ui-icons-common';
import { AppInfoBadge } from '../../components/app-info-badge';
import type { PricingBadgeProps } from '../../components/pricing-badge';
import type {
  ExternalComponentProps,
  ComponentsWithSiteContextProps,
} from '../types';
import {
  useEssentials,
  withEssentialsContext,
} from '../../contexts/essentials-context';
import { withSiteContext } from '../../contexts/site-context';
import { shouldShowPremiumRequired } from '../../components/app-info-badge/app-info-badge.util';
import type { AppPageStickyOptions } from './types';
import { appPageStickyHeaderDataHooks } from './data-hooks';
import s from './app-page-sticky.module.scss';
import type { BaseApp } from '../../types';
import AppBadges from '../../components/badges/app-badges';
import { isAppWithBenefits } from '@wix/app-market-services';

export interface AppPageStickyProps
  extends Required<
      Pick<
        BaseApp,
        'icon' | 'company' | 'installation' | 'pricing' | 'name' | 'appBadges'
      >
    >,
    ExternalComponentProps,
    ComponentsWithSiteContextProps {
  onBack?: () => void;
  onCompanyClick: (companyName: string, companySlug: string) => void;
  mainActionButton: React.ReactNode;
  isVisible: boolean;
  isAppUpgradable: boolean;
  options?: AppPageStickyOptions;
  mode?: 'unlisted' | 'test';
}

function AppPageStickyComp({
  isVisible,
  icon,
  onBack,
  onCompanyClick,
  company,
  installation,
  appBadges,
  pricing,
  name,
  mode,
  mainActionButton,
  isPremiumSite,
  isAppUpgradable,
  options = {},
  languageCode,
}: AppPageStickyProps) {
  const { t } = useEssentials();
  const {
    showCTA = true,
    showAppInfoBadge = true,
    showBackButton = true,
  } = options;

  return (
    <Box
      {...(!isVisible && mode === 'test' && { display: 'none' })}
      {...(!isVisible && mode !== 'test' && { visibility: 'hidden' })}
      verticalAlign="middle"
      backgroundColor="white"
      borderBottom="1px solid #dcdcdc"
      position="sticky"
      zIndex="8"
      align="space-between"
      padding="8px 48px"
      dataHook={appPageStickyHeaderDataHooks.APP_PAGE_STICKY}
      className={s.appPageStickyHeader}
    >
      <Box>
        {showBackButton && onBack && (
          <Box
            align="center"
            justifyItems="center"
            verticalAlign="middle"
            marginRight="12px"
            dataHook={
              appPageStickyHeaderDataHooks.APP_STICKY_HEADER_BACK_BUTTON
            }
          >
            <IconButton
              skin="dark"
              priority="tertiary"
              size="tiny"
              onClick={onBack}
            >
              <ArrowLeftSmall />
            </IconButton>
          </Box>
        )}
        <Box marginRight="12px">
          <Image width="42px" height="42px" src={icon} alt="" />
        </Box>
        <Box direction="vertical">
          <Box>
            <Box>
              <Text size="medium" weight="bold">
                {name}
              </Text>
            </Box>
            <Box className={s.hideOnMobile} paddingLeft="12px" gap="6px">
              <AppBadges
                isInstalled={Boolean(installation?.isInstalled)}
                appName={name ?? ''}
                appBadges={appBadges}
                wixSaleSize="small"
                languageCode={languageCode}
              />
            </Box>
          </Box>
          <Box>
            {company?.name && (
              <Box gap="2px">
                <Text size="small">{t('solution.page.developerName.by')}</Text>
                <TextButton
                  onClick={() =>
                    onCompanyClick(company?.name ?? '', company?.slug ?? '')
                  }
                  size="small"
                >
                  {company?.name}
                </TextButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className={s.hideOnMobile} gap={isAppUpgradable ? '18px' : '12px'}>
        {showAppInfoBadge && (
          <Box verticalAlign="middle">
            <AppInfoBadge
              isAppInstalled={!!installation?.isInstalled}
              isAppBuiltIn={!!installation?.isBuiltIn}
              isAppSetupIncomplete={!!installation?.isSetupIncomplete}
              isAppWithBenefits={isAppWithBenefits(appBadges)}
              pricing={pricing as PricingBadgeProps}
              options={{
                showPremiumRequired: shouldShowPremiumRequired(
                  !!installation?.requirements?.premiumSite,
                  isPremiumSite,
                ),
              }}
              dataHook={
                appPageStickyHeaderDataHooks.APP_STICKY_HEADER_INFO_BADGE
              }
            />
          </Box>
        )}
        {showCTA && mainActionButton}
      </Box>
    </Box>
  );
}
export const AppPageSticky = withSiteContext(
  withEssentialsContext(AppPageStickyComp),
);
